import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import PublicLayout from './layouts/PublicLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import MentalHealthGuide from './pages/public/MentalHealthGuide';
import ContactPage from './pages/public/ContactPage';
import FAQPage from './pages/public/FAQPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';
import EmergencySupport from './pages/public/EmergencySupport';

// Public Self-Help Tools
import SelfAssessmentHub from './pages/public/self-help/SelfAssessmentHub';
import DepressionScreening from './pages/public/self-help/DepressionScreening';
import AnxietyAssessment from './pages/public/self-help/AnxietyAssessment';
import StressCheck from './pages/public/self-help/StressCheck';
import SleepAssessment from './pages/public/self-help/SleepAssessment';
import ADHDScreening from './pages/public/self-help/ADHDScreening';
import SubstanceScreening from './pages/public/self-help/SubstanceScreening';
import MoodTracker from './pages/public/self-help/MoodTracker';
import CrisisSupport from './pages/public/self-help/CrisisSupport';
import MindfulnessHub from './pages/public/self-help/MindfulnessHub';
import CBTWorksheets from './pages/public/self-help/CBTWorksheets';
import StressManagement from './pages/public/self-help/StressManagement';
import ResourceLibrary from './pages/public/self-help/ResourceLibrary';

// Public Appointment Booking
import FindProvider from './pages/public/booking/FindProvider';
import ProviderProfile from './pages/public/booking/ProviderProfile';
import BookAppointment from './pages/public/booking/BookAppointment';
import QuickBooking from './pages/public/booking/QuickBooking';
import RescheduleBooking from './pages/public/booking/RescheduleBooking';
import CancelBooking from './pages/public/booking/CancelBooking';

// Error Pages
import NotFoundPage from './pages/errors/NotFoundPage';
import UnauthorizedPage from './pages/errors/UnauthorizedPage';
import ServerErrorPage from './pages/errors/ServerErrorPage';
import MaintenancePage from './pages/errors/MaintenancePage';
import ScrollToTop from './components/common/ScrollToTop';

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white mx-auto mb-4">
            {/* <svg className="w-8 h-8 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg> */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8 animate-pulse text-white lucide lucide-brain-icon lucide-brain"><path d="M12 18V5"/><path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4"/><path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5"/><path d="M17.997 5.125a4 4 0 0 1 2.526 5.77"/><path d="M18 18a4 4 0 0 0 2-7.464"/><path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517"/><path d="M6 18a4 4 0 0 1-2-7.464"/><path d="M6.003 5.125a4 4 0 0 0-2.526 5.77"/></svg>
          </div>
          <p className="text-gray-600">Loading Rhythm of life...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="mental-health-guide" element={<MentalHealthGuide />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="faq" element={<FAQPage />} />
        <Route path="emergency" element={<EmergencySupport />} />
        
        {/* Self-Help Tools */}
        <Route path="self-help">
          <Route index element={<SelfAssessmentHub />} />
          <Route path="depression-screening" element={<DepressionScreening />} />
          <Route path="anxiety-assessment" element={<AnxietyAssessment />} />
          <Route path="stress-check" element={<StressCheck />} />
          <Route path="sleep-assessment" element={<SleepAssessment />} />
          <Route path="adhd-screening" element={<ADHDScreening />} />
          <Route path="substance-screening" element={<SubstanceScreening />} />
          <Route path="mood-tracker" element={<MoodTracker />} />
          <Route path="crisis-support" element={<CrisisSupport />} />
          <Route path="mindfulness" element={<MindfulnessHub />} />
          <Route path="cbt-worksheets" element={<CBTWorksheets />} />
          <Route path="stress-management" element={<StressManagement />} />
          <Route path="resources" element={<ResourceLibrary />} />
        </Route>
        
        {/* Appointment Booking */}
        <Route path="booking">
          <Route path="find-provider" element={<FindProvider />} />
          <Route path="provider/:providerId" element={<ProviderProfile />} />
          <Route path="book-appointment" element={<BookAppointment />} />
          <Route path="quick-booking" element={<QuickBooking />} />
          <Route path="reschedule" element={<RescheduleBooking />} />
          <Route path="my-bookings/cancel" element={<CancelBooking />} />
        </Route>
        
        {/* Legal */}
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
      </Route>

      {/* Error Pages */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/error" element={<ServerErrorPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
